import { createContext, useCallback, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { BlockNavigationFunction } from 'client-web-sdk';

type BlockNavigationParams = {
  navigationConfirmMessage: string;
  allowNavigationBasePath?: string;
  resetCurrentPage: () => void;
  onNavigationConfirmed?: () => void;
};

export interface BlockNavigationContextValue {
  blockNavigation: (params: BlockNavigationParams) => { unblock: () => void };
  isNavigationBlocked: () => boolean;
}

const BlockNavigationContext = createContext<BlockNavigationContextValue>({
  blockNavigation: () => ({ unblock: () => {} }),
  isNavigationBlocked: () => false,
});

export function BlockNavigationContextProvider({ children }: React.PropsWithChildren<{}>) {
  const navigationBlockCountRef = useRef(0);
  const history = useHistory();

  const blockNavigation = useCallback(
    ({
      navigationConfirmMessage,
      resetCurrentPage,
      allowNavigationBasePath,
      onNavigationConfirmed,
    }: BlockNavigationParams) => {
      navigationBlockCountRef.current++;
      let pendingConfirm = false;

      const unblock = history.block((location) => {
        // special case if we're navigating to the url we're already on
        if (location.pathname === window.location.pathname) {
          // eslint-disable-next-line no-alert
          if (window.confirm(navigationConfirmMessage)) {
            resetCurrentPage();
            onNavigationConfirmed?.();
          }
          return;
        }

        if (allowNavigationBasePath && location.pathname.startsWith(allowNavigationBasePath)) {
          return;
        }

        // otherwise, we hand control over to react-router and just supply the message
        pendingConfirm = true;
        return navigationConfirmMessage;
      });

      const unlisten = history.listen(() => {
        if (pendingConfirm) {
          pendingConfirm = false;
          onNavigationConfirmed?.();
        }
      });

      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        // Multiple ways to prevent navigation for compatibility
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload#example
        e.preventDefault();
        e.returnValue = navigationConfirmMessage;
        return navigationConfirmMessage;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return {
        unblock: () => {
          unblock();
          unlisten();
          window.removeEventListener('beforeunload', handleBeforeUnload);
          navigationBlockCountRef.current--;
        },
      };
    },
    [history],
  );

  const isNavigationBlocked = useCallback(() => navigationBlockCountRef.current > 0, []);

  return (
    <BlockNavigationContext.Provider value={{ blockNavigation, isNavigationBlocked }}>
      {children}
    </BlockNavigationContext.Provider>
  );
}

export function useBlockNavigation(
  navigationConfirmMessage: string,
  allowNavigationBasePath?: string,
): BlockNavigationFunction {
  const { blockNavigation } = useContext(BlockNavigationContext);

  const doBlockNavigation = useCallback(
    ({
      resetCurrentPage,
      onNavigationConfirmed,
    }: {
      resetCurrentPage: () => void;
      onNavigationConfirmed?: () => void;
    }) => {
      return blockNavigation({
        resetCurrentPage,
        navigationConfirmMessage,
        allowNavigationBasePath,
        onNavigationConfirmed,
      });
    },
    [allowNavigationBasePath, blockNavigation, navigationConfirmMessage],
  );

  return doBlockNavigation;
}

export function useIsNavigationBlocked(): () => boolean {
  const { isNavigationBlocked } = useContext(BlockNavigationContext);
  return isNavigationBlocked;
}
